// ArosFlow Academy curriculum.
//
// Written lessons, no video dependency. Every lesson is plain text the reader
// can finish in a few minutes, plus the actions to take afterwards. Education
// only — nothing here is legal, tax or investment advice, and nothing promises
// income.

export interface LessonSection {
  heading: string;
  body: string[];
}

export interface Lesson {
  slug: string;
  title: string;
  summary: string;
  minutes: number;
  sections: LessonSection[];
  actions: string[];
  /** Optional pointer into the software, so the lesson ends somewhere real. */
  tool?: { label: string; to: "/app/analyze" | "/practice" | "/penthouse" };
}

export interface Course {
  slug: string;
  title: string;
  short: string;
  blurb: string;
  lessons: Lesson[];
}

const wholesaling: Course = {
  slug: "wholesaling-startup-system",
  title: "ArosFlow Wholesaling Startup System",
  short: "Wholesaling Startup System",
  blurb:
    "How a wholesale deal actually works end to end: finding a lead worth your time, talking to a seller without a script, underwriting a number you can defend, papering it, and getting it to a buyer and a closing table.",
  lessons: [
    {
      slug: "what-you-are-selling",
      title: "What you are actually selling, and to whom",
      summary:
        "A wholesaler does not sell houses. You sell a contract, and the buyer is not the general public.",
      minutes: 7,
      sections: [
        {
          heading: "The thing you own is a contract",
          body: [
            "When a seller signs a purchase agreement with you, what you own is the right to buy that property on those terms. That right has value when the terms are better than what an investor could get on their own. You then assign that right to a buyer for a fee, or you close on it yourself and resell.",
            "This matters because it sets the whole job. You are not marketing a house. You are creating a contract with enough spread in it that somebody else wants to step into your shoes and pay you to do it.",
          ],
        },
        {
          heading: "Your customer is a cash buyer, not a homeowner",
          body: [
            "Retail buyers need financing, inspections, appraisals and time. Your buyer needs a margin, a scope of work they believe, and a closing date they can hit. Everything you collect on a call should be aimed at whether an investor will say yes: condition, access, title, timeline, price flexibility.",
            "If you cannot describe the property the way a rehabber thinks about it — roof, mechanicals, layout, neighbourhood ceiling — you do not yet have a deal, you have an address.",
          ],
        },
        {
          heading: "Where the fee actually comes from",
          body: [
            "Your fee is the gap between what the seller will accept and what a buyer will pay. It is not a commission and it is not created by negotiating hard. It is created by finding a seller whose priority is something other than maximum price: speed, certainty, no repairs, no showings, no agent, a deadline.",
            "When no such priority exists, there is usually no wholesale deal. Recognising that in ten minutes instead of three weeks is most of the skill.",
          ],
        },
        {
          heading: "Say what you are, plainly",
          body: [
            "Tell the seller you are an investor or that you buy and may assign the contract to another buyer. Most states expect that disclosure, several require it in writing, and a few require a licence for certain activity. Check your state's rules before you market anything, and use a local real estate attorney for your paperwork.",
          ],
        },
      ],
      actions: [
        "Write one sentence describing who your buyer is and the margin they need.",
        "Look up your state's wholesaling and disclosure rules today, not after your first contract.",
        "Draft the one line you will say when a seller asks whether you are an agent.",
      ],
    },
    {
      slug: "reading-the-seller",
      title: "Reading a seller's situation before you talk price",
      summary: "Price is the last question. Situation, timeline and condition come first.",
      minutes: 9,
      sections: [
        {
          heading: "Four things you need before a number",
          body: [
            "Timeline: when do they need this resolved, and what happens on that date. Condition: what is broken, how long has it been broken, who has been living there. Authority: is everyone on title in agreement. Alternatives: have they listed it, has anyone else made an offer.",
            "Until you have all four you cannot underwrite, and any number you give is a guess you will have to retract.",
          ],
        },
        {
          heading: "Ask about consequences, not motivation",
          body: [
            "\"Why are you selling\" gets a rehearsed answer. \"What happens if it is still yours in ninety days\" gets the truth. Consequence questions surface the deadline, the cost of waiting and the person actually driving the decision.",
            "Write down their words. When you later present a number, you will repeat their own reasons back, not your pitch.",
          ],
        },
        {
          heading: "Condition without insulting the house",
          body: [
            "Ask when the roof, furnace, water heater and electrical panel were last touched. Ask what a family member would fix first. Ask if anything has ever leaked. That produces a repair picture without telling a seller their home is a dump — which ends calls.",
          ],
        },
        {
          heading: "Silence and unknowns",
          body: [
            "If you do not know something, say you do not know and say when you will. Do not fill gaps with confident invention. Sellers forgive slow; they do not forgive being wrong twice.",
          ],
        },
      ],
      actions: [
        "Build a four-line call sheet: timeline, condition, authority, alternatives.",
        "Run the seller questionnaire in ArosFlow on your next lead and read the Need Score drivers.",
        "Record one of your own calls and count how early you said a price.",
      ],
      tool: { label: "Practice this on a sample deal", to: "/practice" },
    },
    {
      slug: "arv-repairs-and-the-offer",
      title: "ARV, repairs and the offer you can defend",
      summary: "The number is arithmetic. Your job is defending each input.",
      minutes: 11,
      sections: [
        {
          heading: "The formula",
          body: [
            "ARV − investor profit − closing costs − repairs − your fee = the most you can pay. ArosFlow defaults to a 16.5% investor profit and a 7% closing cushion because that is roughly what a working cash buyer needs. Change them when your market or your buyer says otherwise, and be able to explain why.",
          ],
        },
        {
          heading: "ARV means sold, similar, near, recent",
          body: [
            "Sold — not listed, not pending guesses. Similar — same bed/bath range, same square footage band, same style, same era. Near — same school boundary and the same side of the arterial road. Recent — the last six months, stretched to twelve only when volume is thin.",
            "Three good comps beat nine convenient ones. Note why each comp was chosen; you will be asked.",
          ],
        },
        {
          heading: "Repairs by scope, then by sanity check",
          body: [
            "Start with a per-square-foot tier for the overall condition, then adjust for the big-ticket items you confirmed: roof, HVAC, panel, sewer, foundation, full kitchen and bath replacement. Add a contingency — ten to fifteen percent — because sellers forget things and so do you.",
            "A repair number no contractor would honour is worse than no number. If the range is wide, present it as a range and say what would narrow it.",
          ],
        },
        {
          heading: "Offer range, not a single price",
          body: [
            "Carry three numbers into the call: the most you can pay, the number you would be pleased with, and the point where you walk. Announce the middle, defend it with the inputs, and never cross the walk-away because the conversation got warm.",
          ],
        },
      ],
      actions: [
        "Underwrite one real address in ArosFlow and export the PDF.",
        "For each input, write one sentence on where the figure came from.",
        "Set your walk-away number before you dial, in writing.",
      ],
      tool: { label: "Underwrite a deal", to: "/app/analyze" },
    },
    {
      slug: "contract-title-assignment",
      title: "Contract, title and assignment basics",
      summary: "The paperwork stage is where beginners lose deals and occasionally get sued.",
      minutes: 10,
      sections: [
        {
          heading: "What a purchase agreement must contain",
          body: [
            "Parties exactly as they appear on title, the full legal property description, price, earnest money, closing date, who pays what at closing, an inspection or due-diligence period, and your right to assign. Attorney-prepared state-specific forms only. Do not run a borrowed PDF from a course.",
          ],
        },
        {
          heading: "Inspection periods are not free options",
          body: [
            "A due-diligence window exists so you can verify condition and title, not so you can shop the contract with no intent to close. Using it as a no-risk option is how wholesalers earn a bad name locally, and in several states it is now regulated.",
          ],
        },
        {
          heading: "Title is the real gate",
          body: [
            "Open title the day you sign. Liens, unpaid taxes, code violations, probate, a missing heir, a divorce decree, an unreleased mortgage — all of it surfaces here, and all of it takes time you have not yet budgeted. Work with a title company or attorney who has closed assignments before and will say so plainly.",
          ],
        },
        {
          heading: "Assignment versus double close",
          body: [
            "An assignment transfers your contract for a fee that appears on the settlement statement. A double close is two transactions and hides your spread but costs more and needs funding. Choose based on state rules, the size of the fee and what your title company allows. Disclose the assignment to the seller.",
          ],
        },
      ],
      actions: [
        "Get one state-specific assignable purchase agreement reviewed by a local attorney.",
        "Call two title companies and ask directly whether they close assignments.",
        "Write your standard earnest money amount and where it is held.",
      ],
    },
    {
      slug: "finding-a-real-cash-buyer",
      title: "Finding and qualifying a real cash buyer",
      summary: "A buyers list of two hundred names is worth less than six who have closed this year.",
      minutes: 8,
      sections: [
        {
          heading: "Where actual buyers are",
          body: [
            "Recorded cash purchases in your county over the last twelve months. Rehabs in progress on the streets you work. Local investor meetups. Hard money lenders, who know exactly who is closing. Agents who repeatedly represent investors.",
          ],
        },
        {
          heading: "Qualify before you send anything",
          body: [
            "Ask what they bought most recently and where. Ask their buy box: area, price band, bed/bath, scope of work they take on. Ask how they fund and how fast they close. Ask for proof of funds before you give an address.",
            "A buyer who cannot answer those in one minute has not bought anything recently.",
          ],
        },
        {
          heading: "Send deals the way a buyer reads them",
          body: [
            "Address, ARV with comps attached, repair estimate with scope, your price, access details and closing date. No adjectives. If your numbers hold up twice, you become the person they answer first.",
          ],
        },
        {
          heading: "Protect the relationship both ways",
          body: [
            "Do not shop the same property to twenty people with different prices. Do not promise a closing date your title work cannot support. One blown commitment costs more than one fee.",
          ],
        },
      ],
      actions: [
        "Build a list of six buyers who closed cash in your county this year.",
        "Write your buy-box intake questions and save them in the ArosFlow buyer workspace.",
        "Send one qualified buyer a complete deal packet, even on a deal you do not have yet, as a dry run.",
      ],
    },
  ],
};

const creative: Course = {
  slug: "novations-creative-finance",
  title: "Novations & Creative Finance Startup",
  short: "Novations & Creative Finance",
  blurb:
    "When a discount is not available but terms are. Novations, seller finance and subject-to explained plainly, including the parts that get people in trouble.",
  lessons: [
    {
      slug: "why-no-discount",
      title: "Why some sellers cannot take a cash discount",
      summary: "Equity, not stubbornness, is usually what kills the cash offer.",
      minutes: 7,
      sections: [
        {
          heading: "Run the payoff first",
          body: [
            "Loan balance, arrears, second liens, taxes, judgments and closing costs. If that total is close to market value, there is no room for a wholesale discount no matter how motivated the seller sounds. Find this out on the first call, not after three visits.",
          ],
        },
        {
          heading: "What the seller actually needs",
          body: [
            "Sometimes it is cash today. Often it is the payment gone, the property gone, the credit protected, or a number they can tell their family. Each of those has a structure that fits it, and only the first one requires a deep discount.",
          ],
        },
        {
          heading: "Creative is not a rescue for bad numbers",
          body: [
            "Terms change when and how you pay, not whether the deal works. If the property is worth less than the debt and the payment does not cover costs, no structure fixes that. Say so and move on.",
          ],
        },
      ],
      actions: [
        "Add payoff, arrears and lien questions to your intake.",
        "For your last three dead leads, work out whether equity or motivation was the blocker.",
      ],
    },
    {
      slug: "novation-structure",
      title: "Novation: structure, paperwork and who must be licensed",
      summary: "You improve and sell the house on the seller's title for an agreed net.",
      minutes: 10,
      sections: [
        {
          heading: "How it works",
          body: [
            "The seller agrees to a fixed net at closing. You are permitted to repair and market the property, it is sold at retail to an end buyer, and anything above the seller's net and the costs is yours. Title transfers once, from the seller to the retail buyer.",
            "This fits the seller who has some equity but not enough for a cash discount, and whose house needs work they cannot fund.",
          ],
        },
        {
          heading: "The maths",
          body: [
            "Retail sale price − agent commissions and seller closing costs − repairs you fund − holding costs − the seller's agreed net = your spread. Model it before you sign; the resale cost line alone is usually seven to nine percent.",
          ],
        },
        {
          heading: "Licensing and documentation",
          body: [
            "In several states, marketing a property you do not own for compensation is brokerage. Some states permit novations with an agreement and a licensed agent listing it; others do not. Your compensation must be documented as what it actually is — never dress a brokerage or referral payment as a service fee.",
            "Required paperwork typically includes the novation or purchase agreement, a scope of work and cost authorisation, a clear net-to-seller statement, and written consent to market and repair.",
          ],
        },
        {
          heading: "Risks you carry",
          body: [
            "You fund repairs on someone else's house. The seller can become uncooperative. The market can move during the hold. Price this risk in, and cap your rehab spend.",
          ],
        },
      ],
      actions: [
        "Ask a local attorney whether novations are permitted in your state and under what conditions.",
        "Model one novation in the strategy comparison and compare the spread to a straight assignment.",
      ],
      tool: { label: "Compare exits on a deal", to: "/app/analyze" },
    },
    {
      slug: "seller-finance-terms",
      title: "Seller finance terms that work for both sides",
      summary: "Five variables decide everything: price, down, rate, term and balloon.",
      minutes: 9,
      sections: [
        {
          heading: "Trade price for terms",
          body: [
            "A seller who will not take 70% of value may happily take full asking at 3% over fifteen years with a small down payment. You pay more nominally and less in real terms, and they get monthly income and a number they are proud of.",
          ],
        },
        {
          heading: "Underwrite the payment, not the price",
          body: [
            "Rent minus taxes, insurance, maintenance, vacancy and management must cover the payment with room left. If it only works at 100% occupancy, it does not work.",
          ],
        },
        {
          heading: "Balloons and exits",
          body: [
            "A five-year balloon means you have five years to refinance or sell. Say out loud what happens if you cannot. Longer terms and no balloon are worth paying for.",
          ],
        },
        {
          heading: "Paper it properly",
          body: [
            "Promissory note and mortgage or deed of trust recorded, a servicing company collecting payments, insurance naming the seller, and taxes escrowed. Handshake seller finance ends badly for both parties.",
          ],
        },
      ],
      actions: [
        "Write two term sheets for the same property: cash discount versus full price on terms.",
        "Find a loan servicing company that works in your state and note their fee.",
      ],
    },
    {
      slug: "subject-to-and-due-on-sale",
      title: "Subject-to and due-on-sale, honestly explained",
      summary: "You take title; their loan stays in their name. That is the whole risk.",
      minutes: 9,
      sections: [
        {
          heading: "What subject-to is",
          body: [
            "Title transfers to you, the existing mortgage stays in the seller's name, and you make the payments. It works when the loan rate is good, equity is thin and the seller needs out from under the payment.",
          ],
        },
        {
          heading: "Due-on-sale is real",
          body: [
            "Nearly every mortgage lets the lender call the loan due when title transfers. It is not routinely enforced, but it can be, and rising rates make it likelier. The seller must be told this in writing, in plain words, before signing. If you would not be comfortable reading your disclosure aloud to their adult child, rewrite it.",
          ],
        },
        {
          heading: "Protect the seller",
          body: [
            "Use a servicing company so payments are documented. Keep insurance in force with the correct parties named. Give the seller online visibility of the payment history. Agree in writing what happens if you stop paying, and put a reconveyance or exit clause in it.",
          ],
        },
        {
          heading: "Know when to refuse",
          body: [
            "No, if the seller does not understand the risk. No, if they need the loan off their credit to buy their next home. No, if the payment does not cash flow. Walking away from these is the profitable decision.",
          ],
        },
      ],
      actions: [
        "Write your one-page due-on-sale disclosure in plain language and have an attorney review it.",
        "Decide your rule for when you will not do a subject-to, before a seller talks you into one.",
      ],
    },
    {
      slug: "disclosures-every-time",
      title: "Disclosures you must make, every time",
      summary: "The habit that keeps you in business longer than any script.",
      minutes: 6,
      sections: [
        {
          heading: "The standing list",
          body: [
            "That you are a buyer or investor, not their agent. That you may assign the contract and be paid a fee. What that fee is when the structure requires it. That the existing loan may be called due on a subject-to. That a novation means they still hold title while it is marketed. That your numbers are estimates and which ones could move.",
          ],
        },
        {
          heading: "Written, signed, kept",
          body: [
            "Verbal disclosure is not defensible a year later. One signed page, stored with the file, dated. ArosFlow's decision reports keep your assumptions and sources with the deal for the same reason.",
          ],
        },
        {
          heading: "Marketing claims",
          body: [
            "Do not advertise properties you do not have under contract. Do not imply guaranteed timelines or amounts. Do not use another investor's photos. Regulators and local sellers both notice.",
          ],
        },
        {
          heading: "The standard to hold",
          body: [
            "If the seller found out everything you know a year from now, would they feel dealt with fairly? That is the only test that scales.",
          ],
        },
      ],
      actions: [
        "Assemble your disclosure page and put it in your signing packet permanently.",
        "Review any marketing you already have against the claims list above.",
      ],
    },
  ],
};

export const COURSES: Course[] = [wholesaling, creative];

export function getCourse(slug: string): Course | undefined {
  return COURSES.find((c) => c.slug === slug);
}

export function getLesson(courseSlug: string, lessonSlug: string) {
  const course = getCourse(courseSlug);
  if (!course) return undefined;
  const index = course.lessons.findIndex((l) => l.slug === lessonSlug);
  if (index === -1) return undefined;
  return {
    course,
    lesson: course.lessons[index],
    prev: course.lessons[index - 1] ?? null,
    next: course.lessons[index + 1] ?? null,
    index,
  };
}

export const TOTAL_MINUTES = COURSES.reduce(
  (sum, c) => sum + c.lessons.reduce((s, l) => s + l.minutes, 0),
  0,
);
